import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useQuery } from "@tanstack/react-query";

import { Button } from "@/components/ui/Button";
import { hubModalOverlayProps } from "@/components/ui/HubModalOverlay";
import { Input, Label } from "@/components/ui/Input";
import { SearchableSelect, type SearchableSelectOption } from "@/components/ui/SearchableSelect";
import { SegmentedControl } from "@/components/ui/SegmentedControl";
import { hubModalBodyScrollClassName, hubModalShellClassName } from "@/lib/hubLayoutClassNames";
import {
  fetchCustomerComplaintEvents,
  fetchCustomerContacts,
  fetchCustomerRmaSkus,
  type CreateCustomerComplaintInput,
  type CreateCustomerRmaInput,
} from "@/lib/insideSalesClientData";
import { QualityFreshdeskTicketPicker } from "@/pages/quality/QualityFreshdeskTicketPicker";

const OTHER_WARNING =
  'This is not for RMAs on defective units or other problems. Use "complaint" for those.';

type LineDraft = { sku: string; quantity: string; skuSearch: string };

function useDebounced(value: string): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), 250);
    return () => window.clearTimeout(timer);
  }, [value]);
  return debounced;
}

export function CustomerNewRmaModal(props: {
  defaultCompanyId: number;
  pending: boolean;
  error: string | null;
  onClose: () => void;
  onSubmit: (input: CreateCustomerRmaInput) => void;
}): JSX.Element {
  const companyId = props.defaultCompanyId;
  const [contactId, setContactId] = useState<number | null>(null);
  const [contactLabel, setContactLabel] = useState("");
  const [contactSearch, setContactSearch] = useState("");
  const [addingContact, setAddingContact] = useState(false);
  const [newContactName, setNewContactName] = useState("");
  const [newContactEmail, setNewContactEmail] = useState("");
  const [newContactPhone, setNewContactPhone] = useState("");
  const [reasonKind, setReasonKind] = useState<"complaint" | "other">("complaint");
  const [complaintChoice, setComplaintChoice] = useState<string>("");
  const [complaintLabel, setComplaintLabel] = useState("");
  const [complaintSearch, setComplaintSearch] = useState("");
  const [newComplaint, setNewComplaint] = useState("");
  const [otherReason, setOtherReason] = useState("");
  const [lines, setLines] = useState<LineDraft[]>([{ sku: "", quantity: "1", skuSearch: "" }]);
  const [freshdeskTicketIds, setFreshdeskTicketIds] = useState<number[]>([]);
  const [localError, setLocalError] = useState<string | null>(null);

  const debouncedContact = useDebounced(contactSearch);
  const debouncedComplaint = useDebounced(complaintSearch);
  const activeSkuSearch = lines.map((line) => line.skuSearch).find((value) => value.trim().length >= 2) ?? "";
  const debouncedSku = useDebounced(activeSkuSearch);

  const contactQuery = useQuery({
    queryKey: ["inside-sales", "customers", companyId, "contacts", debouncedContact],
    queryFn: () => fetchCustomerContacts(companyId, debouncedContact),
    enabled: companyId > 0,
  });
  const complaintQuery = useQuery({
    queryKey: ["inside-sales", "customers", companyId, "complaint-events", debouncedComplaint],
    queryFn: () => fetchCustomerComplaintEvents(companyId, debouncedComplaint),
    enabled: companyId > 0 && reasonKind === "complaint",
  });
  const skuQuery = useQuery({
    queryKey: ["inside-sales", "skus", debouncedSku],
    queryFn: () => fetchCustomerRmaSkus(debouncedSku),
    enabled: debouncedSku.trim().length >= 2,
  });

  const contactOptions = useMemo((): SearchableSelectOption[] => {
    return (contactQuery.data ?? []).map((row) => ({
      value: String(row.fsContactId),
      label: row.displayName,
      description: [row.email, row.phone].filter((part) => part != null && part !== "").join(" · ") || null,
    }));
  }, [contactQuery.data]);

  const complaintOptions = useMemo((): SearchableSelectOption[] => {
    const options: SearchableSelectOption[] = [{ value: "new", label: "New complaint" }];
    for (const row of complaintQuery.data ?? []) {
      const summary = row.summary?.trim() || `Complaint ${row.id}`;
      const created = new Date(row.createdAt);
      const createdLabel = Number.isNaN(created.getTime())
        ? ""
        : created.toLocaleDateString("en-US", {
            timeZone: "America/Chicago",
            month: "short",
            day: "numeric",
            year: "numeric",
          });
      options.push({
        value: String(row.id),
        label: summary.length > 80 ? `${summary.slice(0, 80)}...` : summary,
        description: createdLabel || null,
      });
    }
    if (
      complaintChoice !== "" &&
      complaintChoice !== "new" &&
      !options.some((option) => option.value === complaintChoice)
    ) {
      options.push({ value: complaintChoice, label: complaintLabel || `Complaint ${complaintChoice}` });
    }
    return options;
  }, [complaintQuery.data, complaintChoice, complaintLabel]);

  const skuOptions = useMemo((): SearchableSelectOption[] => {
    return (skuQuery.data ?? []).map((row) => ({
      value: row.sku,
      label: row.sku,
      description: row.description,
    }));
  }, [skuQuery.data]);

  function submit(event: React.FormEvent): void {
    event.preventDefault();
    if (props.pending) return;
    if (companyId < 1) {
      setLocalError("Company is required.");
      return;
    }
    if (!addingContact && contactId == null) {
      setLocalError("Contact is required.");
      return;
    }
    if (addingContact && newContactName.trim() === "") {
      setLocalError("Contact name is required.");
      return;
    }
    if (reasonKind === "complaint" && complaintChoice === "") {
      setLocalError("Complaint is required.");
      return;
    }
    if (reasonKind === "complaint" && complaintChoice === "new" && newComplaint.trim() === "") {
      setLocalError("Complaint is required.");
      return;
    }
    if (reasonKind === "other" && otherReason.trim() === "") {
      setLocalError("Reason is required.");
      return;
    }
    const parsedLines: { sku: string; quantity: number }[] = [];
    for (const line of lines) {
      const sku = line.sku.trim();
      const quantity = Number(line.quantity);
      if (sku === "" || !Number.isInteger(quantity) || quantity < 1) {
        setLocalError("Each line item needs a SKU and a quantity of at least 1.");
        return;
      }
      parsedLines.push({ sku, quantity });
    }
    setLocalError(null);
    props.onSubmit({
      companyFsAccountId: companyId,
      contactFsContactId: addingContact ? null : contactId,
      newContactName: addingContact ? newContactName.trim() : null,
      newContactEmail: addingContact ? newContactEmail.trim() || null : null,
      newContactPhone: addingContact ? newContactPhone.trim() || null : null,
      reasonKind,
      qualityEventId:
        reasonKind === "complaint" && complaintChoice !== "new" ? Number(complaintChoice) : null,
      newComplaintSummary: reasonKind === "complaint" && complaintChoice === "new" ? newComplaint.trim() : null,
      otherReason: reasonKind === "other" ? otherReason.trim() : null,
      lines: parsedLines,
      freshdeskTicketIds,
    });
  }

  const message = localError ?? props.error;

  return createPortal(
    <div {...hubModalOverlayProps(true)}>
      <div
        className={hubModalShellClassName("document")}
        role="dialog"
        aria-modal="true"
        aria-labelledby="customer-new-rma-title"
      >
        <div className="border-b border-slate-200 px-4 py-3">
          <h2 id="customer-new-rma-title" className="text-base font-semibold text-slate-900">
            New RMA
          </h2>
        </div>
        <form className="flex min-h-0 flex-1 flex-col" onSubmit={submit}>
          <div className={`${hubModalBodyScrollClassName()} space-y-4 px-4 py-4`}>
            <div className="space-y-1">
              <Label htmlFor="rma-contact">Contact</Label>
              <div className="flex items-start gap-2">
                <div className="min-w-0 flex-1">
                  <SearchableSelect
                    id="rma-contact"
                    value={contactId != null ? String(contactId) : ""}
                    onValueChange={(option) => {
                      setAddingContact(false);
                      setContactId(option == null ? null : Number(option.value));
                      setContactLabel(option?.label ?? "");
                      setContactSearch("");
                    }}
                    options={contactOptions}
                    searchValue={contactSearch}
                    onSearchChange={setContactSearch}
                    placeholder="Search contacts"
                    searchPlaceholder="Search contact name"
                    emptyMessage="No contacts are linked to this company."
                    isLoading={contactQuery.isFetching}
                    disabled={companyId < 1}
                    filterLocally={false}
                    selectedLabelOverride={contactLabel}
                  />
                </div>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    setAddingContact(true);
                    setContactId(null);
                    setContactLabel("");
                  }}
                >
                  Add a new contact
                </Button>
              </div>
              {addingContact ? (
                <div className="grid gap-2 pt-2 sm:grid-cols-3">
                  <div className="space-y-1">
                    <Label htmlFor="rma-new-contact-name">Name</Label>
                    <Input
                      id="rma-new-contact-name"
                      value={newContactName}
                      onChange={(event) => setNewContactName(event.target.value)}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="rma-new-contact-email">Email</Label>
                    <Input
                      id="rma-new-contact-email"
                      value={newContactEmail}
                      onChange={(event) => setNewContactEmail(event.target.value)}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="rma-new-contact-phone">Phone</Label>
                    <Input
                      id="rma-new-contact-phone"
                      value={newContactPhone}
                      onChange={(event) => setNewContactPhone(event.target.value)}
                    />
                  </div>
                </div>
              ) : null}
            </div>

            <div className="space-y-2">
              <Label>Reason for return</Label>
              <SegmentedControl
                aria-label="Reason for return"
                value={reasonKind}
                onChange={setReasonKind}
                options={[
                  { value: "complaint", label: "Complaint" },
                  { value: "other", label: "Other" },
                ]}
              />
              {reasonKind === "complaint" ? (
                <div className="space-y-2">
                  <SearchableSelect
                    id="rma-complaint"
                    value={complaintChoice}
                    onValueChange={(option) => {
                      setComplaintChoice(option?.value ?? "");
                      setComplaintLabel(option?.label ?? "");
                      setComplaintSearch("");
                    }}
                    options={complaintOptions}
                    searchValue={complaintSearch}
                    onSearchChange={setComplaintSearch}
                    placeholder="Search complaints"
                    searchPlaceholder="Search complaint"
                    emptyMessage="No complaints match."
                    isLoading={complaintQuery.isFetching}
                    disabled={companyId < 1}
                    filterLocally={false}
                    selectedLabelOverride={complaintLabel}
                  />
                  {complaintChoice === "new" ? (
                    <textarea
                      id="rma-new-complaint"
                      className="min-h-24 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                      value={newComplaint}
                      onChange={(event) => setNewComplaint(event.target.value)}
                      aria-label="New complaint"
                    />
                  ) : null}
                </div>
              ) : (
                <div className="space-y-2">
                  <p className="text-sm text-slate-700">{OTHER_WARNING}</p>
                  <Label htmlFor="rma-other-reason">Reason</Label>
                  <textarea
                    id="rma-other-reason"
                    className="min-h-24 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                    value={otherReason}
                    onChange={(event) => setOtherReason(event.target.value)}
                  />
                </div>
              )}
            </div>

            <div className="space-y-1">
              <Label htmlFor="rma-freshdesk">Freshdesk tickets</Label>
              <QualityFreshdeskTicketPicker
                id="rma-freshdesk"
                values={freshdeskTicketIds}
                onChange={setFreshdeskTicketIds}
              />
            </div>

            <div className="space-y-2">
              <Label>Line items</Label>
              {lines.map((line, index) => (
                <div key={index} className="grid grid-cols-1 items-end gap-2 sm:grid-cols-[minmax(0,1fr)_6rem_auto]">
                  <div className="space-y-1">
                    <Label htmlFor={`rma-sku-${index}`}>SKU</Label>
                    <SearchableSelect
                      id={`rma-sku-${index}`}
                      value={line.sku}
                      onValueChange={(option) => {
                        setLines((current) =>
                          current.map((row, rowIndex) =>
                            rowIndex === index
                              ? { ...row, sku: option?.value ?? "", skuSearch: "" }
                              : row,
                          ),
                        );
                      }}
                      options={
                        line.sku !== "" && !skuOptions.some((option) => option.value === line.sku)
                          ? [{ value: line.sku, label: line.sku }, ...skuOptions]
                          : skuOptions
                      }
                      searchValue={line.skuSearch}
                      onSearchChange={(query) => {
                        setLines((current) =>
                          current.map((row, rowIndex) =>
                            rowIndex === index ? { ...row, skuSearch: query } : row,
                          ),
                        );
                      }}
                      placeholder="Search SKUs"
                      searchPlaceholder="Search SKU"
                      emptyMessage={line.skuSearch.trim().length < 2 ? "Type at least two characters." : "No SKUs match."}
                      isLoading={skuQuery.isFetching && line.skuSearch.trim().length >= 2}
                      filterLocally={false}
                      selectedLabelOverride={line.sku}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor={`rma-qty-${index}`}>Quantity</Label>
                    <Input
                      id={`rma-qty-${index}`}
                      inputMode="numeric"
                      value={line.quantity}
                      onChange={(event) => {
                        const quantity = event.target.value;
                        setLines((current) =>
                          current.map((row, rowIndex) => (rowIndex === index ? { ...row, quantity } : row)),
                        );
                      }}
                    />
                  </div>
                  <Button
                    type="button"
                    variant="secondary"
                    disabled={lines.length < 2}
                    onClick={() => setLines((current) => current.filter((_, rowIndex) => rowIndex !== index))}
                  >
                    Remove
                  </Button>
                </div>
              ))}
              <Button
                type="button"
                variant="secondary"
                onClick={() => setLines((current) => [...current, { sku: "", quantity: "1", skuSearch: "" }])}
              >
                Add line item
              </Button>
            </div>

            {message ? <p className="text-sm text-rose-700">{message}</p> : null}
          </div>
          <div className="flex justify-end gap-2 border-t border-slate-200 px-4 py-3">
            <Button type="button" variant="secondary" onClick={props.onClose} disabled={props.pending}>
              Cancel
            </Button>
            <Button type="submit" disabled={props.pending}>
              {props.pending ? "Creating…" : "Create RMA"}
            </Button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
}

export function CustomerNewComplaintModal(props: {
  companyId: number;
  pending: boolean;
  error: string | null;
  onClose: () => void;
  onSubmit: (input: CreateCustomerComplaintInput) => void;
}): JSX.Element {
  const [summary, setSummary] = useState("");
  const [contactId, setContactId] = useState<number | null>(null);
  const [contactLabel, setContactLabel] = useState("");
  const [contactSearch, setContactSearch] = useState("");
  const [addingContact, setAddingContact] = useState(false);
  const [newContactName, setNewContactName] = useState("");
  const [newContactEmail, setNewContactEmail] = useState("");
  const [newContactPhone, setNewContactPhone] = useState("");
  const [freshdeskTicketIds, setFreshdeskTicketIds] = useState<number[]>([]);
  const [localError, setLocalError] = useState<string | null>(null);
  const debouncedContact = useDebounced(contactSearch);
  const contactQuery = useQuery({
    queryKey: ["inside-sales", "customers", props.companyId, "contacts", debouncedContact],
    queryFn: () => fetchCustomerContacts(props.companyId, debouncedContact),
    enabled: props.companyId > 0,
  });
  const contactOptions = useMemo(
    (): SearchableSelectOption[] =>
      (contactQuery.data ?? []).map((row) => ({
        value: String(row.fsContactId),
        label: row.displayName,
        description: [row.email, row.phone].filter((part) => part != null && part !== "").join(" · ") || null,
      })),
    [contactQuery.data],
  );

  function submit(event: React.FormEvent): void {
    event.preventDefault();
    if (props.pending) return;
    if (summary.trim() === "") {
      setLocalError("Summary is required.");
      return;
    }
    if (!addingContact && contactId == null) {
      setLocalError("Contact is required.");
      return;
    }
    if (addingContact && newContactName.trim() === "") {
      setLocalError("Contact name is required.");
      return;
    }
    setLocalError(null);
    props.onSubmit({
      summary: summary.trim(),
      contactFsContactId: addingContact ? null : contactId,
      newContactName: addingContact ? newContactName.trim() : null,
      newContactEmail: addingContact ? newContactEmail.trim() || null : null,
      newContactPhone: addingContact ? newContactPhone.trim() || null : null,
      freshdeskTicketIds,
    });
  }

  const message = localError ?? props.error;
  return createPortal(
    <div {...hubModalOverlayProps(true)}>
      <div className={hubModalShellClassName("form")} role="dialog" aria-modal="true" aria-labelledby="customer-new-complaint-title">
        <div className="border-b border-slate-200 px-4 py-3">
          <h2 id="customer-new-complaint-title" className="text-base font-semibold text-slate-900">
            New complaint
          </h2>
        </div>
        <form className="flex min-h-0 flex-1 flex-col" onSubmit={submit}>
          <div className={`${hubModalBodyScrollClassName()} space-y-3 px-4 py-4`}>
            <div className="space-y-1">
              <Label htmlFor="complaint-contact">Contact</Label>
              <div className="flex items-start gap-2">
                <div className="min-w-0 flex-1">
                  <SearchableSelect
                    id="complaint-contact"
                    value={contactId != null ? String(contactId) : ""}
                    onValueChange={(option) => {
                      setAddingContact(false);
                      setContactId(option == null ? null : Number(option.value));
                      setContactLabel(option?.label ?? "");
                      setContactSearch("");
                    }}
                    options={contactOptions}
                    searchValue={contactSearch}
                    onSearchChange={setContactSearch}
                    placeholder="Search contacts"
                    searchPlaceholder="Search contact name"
                    emptyMessage="No contacts are linked to this company."
                    isLoading={contactQuery.isFetching}
                    filterLocally={false}
                    selectedLabelOverride={contactLabel}
                  />
                </div>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    setAddingContact(true);
                    setContactId(null);
                    setContactLabel("");
                  }}
                >
                  Add a new contact
                </Button>
              </div>
              {addingContact ? (
                <div className="grid gap-2 pt-2">
                  <Input
                    aria-label="Name"
                    placeholder="Name"
                    value={newContactName}
                    onChange={(event) => setNewContactName(event.target.value)}
                  />
                  <Input
                    aria-label="Email"
                    placeholder="Email"
                    value={newContactEmail}
                    onChange={(event) => setNewContactEmail(event.target.value)}
                  />
                  <Input
                    aria-label="Phone"
                    placeholder="Phone"
                    value={newContactPhone}
                    onChange={(event) => setNewContactPhone(event.target.value)}
                  />
                </div>
              ) : null}
            </div>
            <div className="space-y-1">
              <Label htmlFor="complaint-freshdesk">Freshdesk tickets</Label>
              <QualityFreshdeskTicketPicker
                id="complaint-freshdesk"
                values={freshdeskTicketIds}
                onChange={setFreshdeskTicketIds}
              />
            </div>
            <label className="block space-y-1 text-sm text-slate-700">
              <span>Summary</span>
              <textarea
                className="min-h-24 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                value={summary}
                onChange={(event) => setSummary(event.target.value)}
              />
            </label>
            {message ? <p className="text-sm text-rose-700">{message}</p> : null}
          </div>
          <div className="flex justify-end gap-2 border-t border-slate-200 px-4 py-3">
            <Button type="button" variant="secondary" onClick={props.onClose} disabled={props.pending}>
              Cancel
            </Button>
            <Button type="submit" disabled={props.pending}>
              {props.pending ? "Creating…" : "Create complaint"}
            </Button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
}
